/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import {
    CertModel,
    CertsApiService,
    haproxyTimeValidation,
    Layer7ApiService,
    LbErrorService,
    SIPApiService,
    SipModel
} from '../../../shared';
import { Component, OnInit } from '@angular/core';
import { complexPasswordValidation } from '../../../../../core/directives/validation';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { L7GlobalServiceModel } from '../../../shared/models/l7.global.service.model';
import { LoadingService, LogService, RbacService, UtilsService, ValidationService } from '../../../../../core/services';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { integerValidation, portRangeValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-advanced-services',
    templateUrl: './advancedServices.component.html',
    styleUrls: ['./advancedServices.component.scss']
})

export class AdvancedServicesComponent extends BaseComponent implements OnInit {
    /**
     * Store the active tab ID.
     */
    public activeTabId: string = 'l7';

    /**
     * To store the form group for Layer 7 globals.
     */
    public l7GlobalForm: FormGroup;

    /**
     * To store the SSL certificate data.
     */
    public sipsData: SipModel[];

    /**
     * To store the SSL certificate data.
     */
    public SSLCertificateData: CertModel[];

    /**
     * To store the haproxy global data.
     */
    public haproxyGlobalData: L7GlobalServiceModel;

    /**
     * The link location for the haproxy stats page.
     */
    public haproxyStatsURL: string;

    /**
     * Dropdown content object.
     */
    public dropdown: any = {
        logLevel: [],
        tls: [],
        sips: [],
        sslCerts: []
    };

    /**
     * Page finished loading flag.
     */
    public pageReady: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Global Services Object
     */
    private l7GlobalObj: L7GlobalServiceModel = <L7GlobalServiceModel>{};

    /**
     * To store the initial L7 global form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private certsApiService: CertsApiService,
        private formBuilder: FormBuilder,
        private layer7ApiService: Layer7ApiService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private rbacService: RbacService,
        private sipsApiService: SIPApiService,
        private utilsService: UtilsService
    ) {
        super();
        this.setUpPageData();
    }

    public ngOnInit(): void {
        // Run haproxy global call
        this.layer7ApiService.getGlobalOptions()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (hapGlobalOpts: L7GlobalServiceModel): void => {
                    this.l7GlobalObj = hapGlobalOpts;
                    this.generateHAPStatsLink();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdvancedServicesComponent: ngOnInit', 'Error getting data', error);
                }
            });
    }

    /**
     * Set the active tab ID.
     * @param $event
     */
    public setActiveTabId($event: any): void {
        this.activeTabId = $event?.id;
    }

    /**
     * Subscribe to stat changes.
     * Generate the link location for the haproxy stats page.
     */
    public generateHAPStatsLink(): void {
        this.haproxyStatsURL = this.utilsService.getHaProxyStatsUrl(this.l7GlobalObj);
    }

    /**
     * Submit the Layer 7 global data.
     */
    public submitL7Global(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const model = <L7GlobalServiceModel>this.l7GlobalForm.getRawValue();
            this.layer7ApiService.editGlobalOptions(model)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.haproxyGlobalData = <L7GlobalServiceModel>data.global;
                        this.patchGlobalData(this.haproxyGlobalData);
                        this.l7GlobalForm.markAsPristine();
                        this.l7GlobalForm.markAsUntouched();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.l7GlobalForm, error.error.errors);
                        this.logService.error('AdvancedServicesComponent: submitL7Global', 'Error submitting data', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                }).add((): void => {
                this.loadingService.loadingButtonEmitter.next(false);
            });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            haproxyGlobalData: this.layer7ApiService.getGlobalOptions(),
            sips: this.sipsApiService.getStaticIPs(),
            sslCerts: this.certsApiService.getCerts()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['haproxyGlobalData']) {
                        this.haproxyGlobalData = dataGroup['haproxyGlobalData'];
                    }

                    if (dataGroup['sips']) {
                        this.sipsData = dataGroup['sips'];
                    }

                    if (dataGroup['sslCerts']) {
                        this.SSLCertificateData = dataGroup['sslCerts'];
                    }

                    this.setupFormGroup();
                    this.setUpDropdownContent();
                    this.setupFormSubscribers();

                    this.pageReady = true;

                    this.registerButtons();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdvancedServicesComponent: setUpPageData', 'Error getting page data', error);
                }
            });
    }

    /**
     * Setup page form.
     * @private
     */
    private setupFormGroup(): void {
        this.l7GlobalForm = this.formBuilder.group({
            loggingLevel: [null],
            connectionTimeout: [null, [Validators.required, haproxyTimeValidation]],
            clientTimeout: [null, [Validators.required, haproxyTimeValidation]],
            serverTimeout: [null, [Validators.required, haproxyTimeValidation]],
            maximumConns: [null, [Validators.required, integerValidation, Validators.min(1)]],
            haproxyStatsEnable: [null],
            haproxyStatsPassword: [null, [complexPasswordValidation]],
            haproxyStatsSipId: [{
                value: this.haproxyGlobalData.haproxyStatsSipId, disabled: !this.haproxyGlobalData.haproxyStatsEnable
            }, [Validators.required]],
            haproxyStatsPort: [{
                value: null, disabled: !this.haproxyGlobalData.haproxyStatsEnable
            }, [Validators.required, portRangeValidation]],
            haproxyStatsAdvanced: [{ value: null, disabled: !this.haproxyGlobalData.haproxyStatsEnable }],
            haproxyStatsSSL: [{ value: null, disabled: !this.haproxyGlobalData.haproxyStatsEnable }],
            haproxyStatsSSLMinVer: [{ value: null, disabled: !this.haproxyGlobalData.haproxyStatsEnable }],
            haproxyStatsSSLMaxVer: [{ value: null, disabled: !this.haproxyGlobalData.haproxyStatsEnable }],
            haproxyStatsCertId: [{
                value: this.haproxyGlobalData.haproxyStatsCertId, disabled: !this.haproxyGlobalData.haproxyStatsEnable
            }],
            requestBuffLen: [null, [Validators.required, Validators.min(1), integerValidation]],
            headerBuffLen: [null, [Validators.required, Validators.min(1), integerValidation]],
            enablePersistenceReplication: [null],
            persistenceReplicationPort: [{ value: null, disabled: !this.haproxyGlobalData.enablePersistenceReplication },
                [portRangeValidation, integerValidation]
            ],
            emailAlertFrom: [null, [ValidationService.emailValidator]],
            emailAlertTo: [null, [ValidationService.emailValidator]],
            emailAlertMailer: [null],
            emailMailerPort: [null, [portRangeValidation, integerValidation]],
            enableThreads: [null],
            numberOfThreads: [{ value: null, disabled: !this.haproxyGlobalData.enableThreads },
                [Validators.min(1), Validators.max(10), integerValidation]
            ],
            sslCacheLifetime: [null],
            sslCacheSize: [null]
        });

        this.patchGlobalData(this.haproxyGlobalData);
    }

    /**
     * Patch global data to form.
     * @param haproxyGlobalData
     * @private
     */
    private patchGlobalData(haproxyGlobalData: L7GlobalServiceModel): void {
        const globalForm: FormGroup = this.l7GlobalForm;
        globalForm.patchValue(haproxyGlobalData);
        this.initialFormData = globalForm.value;
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        const form: FormGroup = this.l7GlobalForm;
        form.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (formData: any): void => {
                    this.utilsService.debounce('form', (): void => {
                        if (this.utilsService.hasFormDataChanged(this.initialFormData, formData)) {
                            this.l7GlobalForm.markAsDirty();
                        } else {
                            this.l7GlobalForm.markAsPristine();
                        }
                    });
                }
            });

        form.get('haproxyStatsEnable').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: checkboxState => {
                    this.setHaproxyStatFormFields(checkboxState);
                }
            });

        form.get('haproxyStatsSSL').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: checkboxState => {
                    if (checkboxState) {
                        form.get('haproxyStatsCertId').setValidators(Validators.required);
                        form.get('haproxyStatsCertId').updateValueAndValidity();
                    } else {
                        form.get('haproxyStatsCertId').clearValidators();
                        form.get('haproxyStatsCertId').updateValueAndValidity();
                    }
                }
            });

        form.get('enableThreads').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: checkboxValue => {
                    const toggled: 'enable' | 'disable' = checkboxValue ? 'enable' : 'disable';
                    form.get('numberOfThreads')[toggled]();
                }
            });

        form.get('enablePersistenceReplication').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: checkboxValue => {
                    const toggled: 'enable' | 'disable' = checkboxValue ? 'enable' : 'disable';
                    form.get('persistenceReplicationPort')[toggled]();
                }
            });
    }

    /**
     * Set HA Proxy stats related inputs in the form.
     * @param checkboxState
     * @private
     */
    private setHaproxyStatFormFields(checkboxState: boolean): void {
        const formFields = this.l7GlobalForm;

        if (checkboxState) {
            formFields.get('haproxyStatsSipId').enable();
            formFields.get('haproxyStatsPort').enable();
            formFields.get('haproxyStatsPassword').enable();
            formFields.get('haproxyStatsAdvanced').enable();
            formFields.get('haproxyStatsSSL').enable();
            formFields.get('haproxyStatsCertId').enable();
            formFields.get('haproxyStatsSSLMinVer').enable();
            formFields.get('haproxyStatsSSLMaxVer').enable();
        } else {
            formFields.get('haproxyStatsPassword').disable();
            formFields.get('haproxyStatsSipId').disable();
            formFields.get('haproxyStatsPort').disable();
            formFields.get('haproxyStatsAdvanced').disable();
            formFields.get('haproxyStatsSSL').disable();
            formFields.get('haproxyStatsCertId').disable();
            formFields.get('haproxyStatsSSLMinVer').disable();
            formFields.get('haproxyStatsSSLMaxVer').disable();
        }
    }

    /**
     * Set up dropdown content.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.logLevel = [
            <DropdownModel>{ key: 'off', value: 'Off' },
            <DropdownModel>{ key: 'healthChecks', value: 'Health Check' },
            <DropdownModel>{ key: 'errors', value: 'Errors' },
            <DropdownModel>{ key: 'debug', value: 'Debug' }
        ];

        this.dropdown.tls = [
            <DropdownModel>{ key: 'TLSv1.0', value: 'TLSv1.0' },
            <DropdownModel>{ key: 'TLSv1.1', value: 'TLSv1.1' },
            <DropdownModel>{ key: 'TLSv1.2', value: 'TLSv1.2' },
            <DropdownModel>{ key: 'TLSv1.3', value: 'TLSv1.3' },
        ];

        this.dropdown.sips = this.utilsService.convertArrayToDropDown(this.sipsData, 'id', 'ip');
        this.dropdown.sslCerts = this.utilsService.convertArrayToDropDown(this.SSLCertificateData, 'id', 'name');
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'advanced-services-update': [{ path: '/haproxy/global', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['advanced-services-update']) {
            this.l7GlobalForm?.disable();
        }
    }
}
